import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { isAppFileUrl, toInternalRoute } from '@core/services/deep-link/app-links';
import { InAppBrowserService } from '@core/services/in-app-browser/in-app-browser.service';
import { isMobile } from '@shared/utils/platform.utils';

// Android delivers a cold start through `getLaunchUrl`, iOS through
// `appUrlOpen`, and both can fire for the same tap.
const DUPLICATE_WINDOW_MS = 1000;

/**
 * Universal / app links (Capacitor).
 *
 * Turns an incoming `https://myindiba.indiba.com/...` (or `myindiba://...`) url
 * into a router navigation. Authentication is not checked here: the route
 * guards send the user to the login screen keeping the url, and the login
 * brings them back to it.
 *
 * What counts as one of our urls lives in `app-links`, shared with
 * `InAppBrowserService` so a link tapped inside the webview and one delivered by
 * the system end up in the same place.
 */
@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {
  private listenersReady = false;
  private lastHandled: { url: string; at: number } | null = null;

  constructor(
    private readonly router: Router,
    private readonly inAppBrowser: InAppBrowserService
  ) {}

  async setupListeners(): Promise<void> {
    if (!isMobile() || this.listenersReady) {
      return;
    }
    this.listenersReady = true;

    await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => this.open(event.url));

    const launch = await App.getLaunchUrl();
    launch?.url && this.open(launch.url);
  }

  private open(rawUrl: string): void {
    if (this.isDuplicate(rawUrl)) {
      return;
    }

    // The verified hosts also serve the resource files, and a file has no route
    // to go to: an autoverified link to a pdf used to land on a 404.
    if (isAppFileUrl(rawUrl)) {
      this.inAppBrowser.open(rawUrl);
      return;
    }

    const path = toInternalRoute(rawUrl);
    path && this.router.navigateByUrl(path);
  }

  private isDuplicate(rawUrl: string): boolean {
    const now = Date.now();
    const duplicate =
      this.lastHandled?.url === rawUrl && now - this.lastHandled.at < DUPLICATE_WINDOW_MS;

    this.lastHandled = { url: rawUrl, at: now };

    return duplicate;
  }
}
